import * as service from "../service";
import { store } from "../../store";
import { controlLoading } from "../../slices/app.slice";
import type { ServiceResponse } from "../types";

export type StudentProfile = Record<string, any>;

/** Legacy, unpaginated payload (every row). */
export type GetAllStudentsResult = StudentProfile[];

/**
 * Verification states, derived server-side from document evidence:
 * `none` = no document uploaded, `pending` = uploaded but not yet approved,
 * and the three approved combinations. These five form a complete partition
 * of the student list.
 */
export type StudentVerificationFilter =
  | "all"
  | "none"
  | "pending"
  | "sc_verified"
  | "hkid_verified"
  | "both_verified";

export type GetAllStudentsParams = {
  page?: number;
  size?: number;
  q?: string;
  verification?: StudentVerificationFilter;
  active?: "all" | "active" | "inactive";
  university?: string[];
};

export type GetAllStudentsPagedResult = {
  /** One page of rows, already filtered/sorted by the server. */
  data: StudentProfile[];
  /** Rows matching the active search + filters (not the page size). */
  total: number;
  /** Every student, ignoring search + filters. Backs the tab label / stat card. */
  overallTotal: number;
  /** Students who uploaded a document that is still awaiting review. */
  pendingCount: number;
  page: number;
  size: number;
  hasMore: boolean;
};

/**
 * Fetch student information (admin only).
 * Backend: uninfo-api/src/api/student/getAllStudents.js
 *
 * Called without `params` it keeps the legacy whole-list behaviour for callers
 * that iterate every row. Pass `params` to opt into server-side pagination and
 * filtering, which is what bounded payloads need.
 */
export const GetAllStudentsAPI = async (
  params?: GetAllStudentsParams,
): Promise<ServiceResponse<GetAllStudentsResult | GetAllStudentsPagedResult>> => {
  try {
    const admin = store.getState().auth.admin;
    const resp = (await service.call(
      "GET",
      "/api/student/getAllStudents",
      {
        user_id: admin.id,
        ...(params ? { scope: "page" } : {}),
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.page ? { page: params.page } : {}),
        ...(params?.size ? { size: params.size } : {}),
        ...(params?.verification && params.verification !== "all"
          ? { verification: params.verification }
          : {}),
        ...(params?.active && params.active !== "all" ? { active: params.active } : {}),
        ...(params?.university?.length ? { university: params.university.join(",") } : {}),
      },
    )) as ServiceResponse<GetAllStudentsResult | GetAllStudentsPagedResult>;
    if (resp?.status === 0) {
      store.dispatch(controlLoading({ showLoading: false }));
    }
    return resp;
  } catch (err) {
    console.error(`GetAllStudentsAPI ${err}`);
    return { status: -1, error: err };
  }
};
